import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Phone, Hammer, PaintRoller, Layers, Ruler, Droplets, CloudOff } from "lucide-react";
import { SERVICES_PAGE } from "@/constants/testIds";
import useSeo from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "(705) 796-4025";
const PHONE_HREF = "tel:+17057964025";

const ICONS = {
  "drywall-repair": Hammer,
  "patching": PaintRoller,
  "texture-matching": Layers,
  "ceiling-repair": Ruler,
  "water-damage": Droplets,
  "popcorn-removal": CloudOff,
};

const IMAGES = {
  "drywall-repair": "/drywall-repair.png",
  "patching": "/patching.png",
  "texture-matching": "/texture-matching.jpg",
  "ceiling-repair": "/ceiling-repair.jpg",
  "water-damage": "/water-damage.webp",
  "popcorn-removal": "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const ROW_BG = [
  "var(--cream)",
  "var(--cream-soft)",
];

export default function Services() {
  const [services, setServices] = useState([]);
  useSeo({
    title: "Drywall Repair in Wasaga Beach — Services · Southern Georgian Bay Repair Expert",
    description:
      "Drywall Repair in Wasaga Beach and area from Smooth Finish, your Southern Georgian Bay Repair Expert. Patching, ceiling repair, water damage, popcorn removal. Call (705) 796-4025.",
    path: "/services",
  });
  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid={SERVICES_PAGE.root}>
      {/* HEADER */}
      <section className="pt-12 pb-20 relative overflow-hidden">
        <div className="absolute -top-32 right-0 w-[460px] h-[460px] rounded-full bg-[var(--mustard)] opacity-40 blur-3xl"/>
        <div className="container-x relative">
          <span className="chip">SERVICES</span>
          <h1 className="display-hero mt-5">
            Drywall work,<br/>done <span className="text-[var(--coral)]">properly</span>.
          </h1>
          <p className="mt-6 text-xl text-[var(--ink-soft)] max-w-2xl">
            Specializing in precision patching and seamless drywall repairs, we bring your damaged drywall surface back to a smooth unblemished condition.
          </p>
        </div>
      </section>

      {/* Alternating rows */}
      <section>
        <div className="container-x space-y-6">
          {services.map((s, idx) => {
            const Icon = ICONS[s.slug] || Hammer;
            const reverse = idx % 2 === 1;
            return (
              <div
                key={s.slug}
                data-testid={SERVICES_PAGE.row(s.slug)}
                className="rounded-[36px] p-6 sm:p-10 lg:p-14 relative overflow-hidden"
                style={{ background: ROW_BG[idx % 2] }}
              >
                <div className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center">
                        <Icon size={24}/>
                      </div>
                      <div className="font-display font-bold text-3xl text-[var(--coral)]">0{idx + 1}</div>
                    </div>
                    <h2 className="display-xl">{s.title}</h2>
                    <p className="mt-5 text-lg text-[var(--ink-soft)] max-w-xl">{s.summary}</p>
                    <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      {s.details.map((d) => (
                        <li key={d} className="flex items-start gap-2 text-[var(--ink)] font-medium">
                          <CheckCircle2 size={18} className="mt-0.5 text-[var(--coral)] shrink-0"/> {d}
                        </li>
                      ))}
                    </ul>
                    <a href={PHONE_HREF} className="btn-primary mt-9">
                      <Phone size={18}/> Call Mike for a free estimate
                    </a>
                  </div>
                  <div className="lg:col-span-5">
                    <div className={`${s.slug === "texture-matching" ? "aspect-[3/5]" : "aspect-[4/5]"} photo-modern relative`}>
                      <img
                        src={IMAGES[s.slug]}
                        alt={s.title}
                      />
                      {s.slug === "texture-matching" && (
                        <span
                          aria-hidden="true"
                          className="absolute left-1/2 -translate-x-1/2 pointer-events-none select-none text-[var(--ink)] font-black uppercase text-3xl sm:text-4xl"
                          style={{ bottom: "10%", fontFamily: "'Manrope', system-ui, sans-serif", letterSpacing: "0.02em" }}
                        >
                          KNOCKDOWN
                        </span>
                      )}
                      <div className="absolute bottom-4 left-4 right-4 bg-[var(--ink)] text-[var(--cream)] px-4 py-3 rounded-2xl flex items-center justify-between">
                        <span className="text-xs uppercase tracking-widest font-semibold">{s.title}</span>
                        <span className="font-display font-bold text-[var(--coral)]">0{idx + 1}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section">
        <div className="container-x">
          <div className="rounded-[40px] bg-[var(--teal)] text-[var(--cream)] p-12 sm:p-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="chip cream-on-dark">Not sure which?</span>
              <h3 className="display-xl mt-5 text-[var(--cream)]">Just give us a call. We'll figure it out.</h3>
              <p className="mt-6 text-[#E8E1D2]/80 max-w-xl">No matter how big the hole or how tricky the repair, we'll restore your ceiling and walls to look brand new.</p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <a href={PHONE_HREF} className="btn-coral !text-lg"><Phone size={20}/> Call Mike for a free estimate</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
